import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzAutocompleteTriggerDirective } from 'ng-zorro-antd/auto-complete';

@Component({
  selector: 'app-text-input',
  templateUrl: './text.component.html',
  styles: [],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true
    }
  ]
})
export class TextInputComponent implements ControlValueAccessor, OnChanges {
  @Input() class: string = '';
  @Input() value: any;
  @Input() allowEdit = true;
  @Input() maxlength: string | number | null = 100;
  @Input() isRequired: boolean = false;
  @Input() borderless = false;
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() autoCompleteData: string[] = [];
  @Input() tabindex: string | null = null;
  @Input() suffixButtonIcon?: string | null = null;

  @Output() readonly suffixButton: EventEmitter<void> = new EventEmitter();
  @Output() readonly blur = new EventEmitter();

  @ViewChild('input') inputRef!: ElementRef<HTMLInputElement>;

  // Reference to nz-autocomplete trigger directive to programmatically control the panel
  @ViewChild(NzAutocompleteTriggerDirective)
  private autoTrigger?: NzAutocompleteTriggerDirective;

  get readonly() {
    if (this.disabled || !this.allowEdit) {
      return true;
    }
    return false;
  }

  onChange: any = () => { };
  onTouched: any = () => { };
  constructor(
    private cdr: ChangeDetectorRef,

    // Host element reference to check if focus is within this component
    private host: ElementRef<HTMLElement>
  ) { }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.allowEdit = !isDisabled;
  }

  onModelChange(val: any) {
    this.value = val;
    this.onChange(val);
    this.cdr.markForCheck();
  }

  onBlur(e: any) {
    this.blur.emit(e);
  }

  onClick(e: any) {
    if (!window.getSelection()?.toString()) {
      e.select();
    }
  }

  onInputFocus(event: FocusEvent) {
    (event.target as HTMLInputElement).select();
  }

  focus() {
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.focus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Early return if no autocomplete data change or required dependencies are missing
    if (
      !changes['autoCompleteData'] ||
      !this.autoCompleteData?.length ||
      this.autoTrigger == null ||
      !this.inputRef
    ) {
      return;
    }

    const prev = (changes['autoCompleteData'].previousValue as string[] | undefined) || [];
    const current = this.autoCompleteData || [];

    // Only reopen if data changed AND current has data AND previous was different
    // This handles component reuse scenarios where prev might already have data
    if (prev.length === current.length &&
      prev.every((item, index) => item === current[index])) {
      // Data didn't actually change (same array reference or same content)
      return;
    }

    // Only reopen the panel if the input or its host element has focus
    const activeElement = document.activeElement;
    const inputFocused = activeElement === this.inputRef?.nativeElement;
    const hostFocused = !!activeElement && this.host.nativeElement.contains(activeElement);

    if (!inputFocused && !hostFocused) {
      return;
    }

    // Use Promise.resolve() to defer panel opening until after Angular's change detection cycle completes
    // This ensures the autocomplete dropdown is properly rendered with the new data before opening
    Promise.resolve().then(() => this.autoTrigger?.openPanel());
  }
}
